import { createWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

export type MarketType = 'spread' | 'total' | 'moneyline' | 'future_team_champion' | 'player_prop';
export type TeamType = 'home' | 'away';
export type Side = 'yes' | 'no';

export type MarketSelection = {
  marketGroupId: string;
  marketId: string;
  marketType: MarketType;
  teamType: TeamType;
  side: Side;
};

type MarketSelectionState = {
  selections: MarketSelection[];
  amount: string;
  isDrawerOpen: boolean;

  addSelection: (selection: MarketSelection) => void;
  removeSelection: (marketId: string) => void;
  toggleSelection: (selection: MarketSelection) => void;
  clearSelections: () => void;
  setAmount: (amount: string) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  setDrawerOpen: (open: boolean) => void;
  restoreSelection: (selection: MarketSelection, amount: string) => void;
  reset: () => void;
};

const initialState: Pick<MarketSelectionState, 'selections' | 'amount' | 'isDrawerOpen'> = {
  selections: [],
  amount: '',
  isDrawerOpen: false,
};

export const useMarketSelectionStore = createWithEqualityFn<MarketSelectionState>()(
  devtools(
    (set) => ({
      ...initialState,

      addSelection: (selection) =>
        set(
          (state) => ({
            selections: [...state.selections, selection],
          }),
          false,
          'addSelection'
        ),

      removeSelection: (marketId) =>
        set(
          (state) => ({
            selections: state.selections.filter((s) => s.marketId !== marketId),
          }),
          false,
          'removeSelection'
        ),

      toggleSelection: (selection) =>
        set(
          (state) => {
            const currentSelection = state.selections[0];
            const existingIndex = state.selections.findIndex(
              (s) =>
                s.marketGroupId === selection.marketGroupId &&
                s.marketId === selection.marketId &&
                s.marketType === selection.marketType &&
                s.teamType === selection.teamType &&
                s.side === selection.side
            );

            if (existingIndex >= 0) {
              return {
                selections: state.selections.filter((_, i) => i !== existingIndex),
              };
            }

            const marketGroupChanged =
              currentSelection && currentSelection.marketGroupId !== selection.marketGroupId;

            return {
              selections: [selection],
              ...(marketGroupChanged ? { amount: '' } : {}),
            };
          },
          false,
          'toggleSelection'
        ),

      clearSelections: () =>
        set(
          {
            selections: [],
            amount: '',
          },
          false,
          'clearSelections'
        ),

      setAmount: (amount) =>
        set(
          {
            amount,
          },
          false,
          'setAmount'
        ),

      openDrawer: () =>
        set(
          {
            isDrawerOpen: true,
          },
          false,
          'openDrawer'
        ),

      closeDrawer: () =>
        set(
          {
            isDrawerOpen: false,
          },
          false,
          'closeDrawer'
        ),

      setDrawerOpen: (open) =>
        set(
          {
            isDrawerOpen: open,
          },
          false,
          'setDrawerOpen'
        ),

      restoreSelection: (selection, amount) =>
        set(
          (state) => {
            if (state.selections.length > 0) {
              return state;
            }
            return {
              selections: [selection],
              amount,
              isDrawerOpen: true,
            };
          },
          false,
          'restoreSelection'
        ),
      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'MarketSelectionStore' }
  ),
  shallow
);

export function useCurrentSelection(): MarketSelection | null {
  return useMarketSelectionStore((state) =>
    state.selections.length > 0 ? state.selections[0] : null
  );
}

export function useHasSelection(): boolean {
  return useMarketSelectionStore((state) => state.selections.length > 0);
}

export function useSelectionAmount(): string {
  return useMarketSelectionStore((state) => state.amount);
}

export function useIsDrawerOpen(): boolean {
  return useMarketSelectionStore((state) => state.isDrawerOpen);
}

export function useDrawerActions() {
  const openDrawer = useMarketSelectionStore((state) => state.openDrawer);
  const closeDrawer = useMarketSelectionStore((state) => state.closeDrawer);
  const setDrawerOpen = useMarketSelectionStore((state) => state.setDrawerOpen);
  return { openDrawer, closeDrawer, setDrawerOpen };
}
